import React, { useEffect } from "react"
import { Outlet } from "react-router-dom"
import { useDispatch } from "react-redux"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import CustomPagination from "components/common/CustomPagination"
import { useTranslation } from "react-i18next"
import CrewMembersTable from "./crewMembersTable"
import CrewFilters from "./components/CrewFilters"
import FilterChips from "ui/FilterChips"
import { useCrewFilters } from "./hooks/useCrewFilters"
import { Spin } from "antd"
import EditCrewMemberModal from "./crewDetail/components/EditCrewMemberModal"
import DeactivateDeleteModals from "./crewDetail/components/DeactivateDeleteModals"
import { getCrewProfiles } from "store/crew-profiles"
import { getRoles } from "store/roles"
import { useCrewMemberEdit } from "./hooks/useCrewMemberEdit"
import { useCrewDeactivateModal } from "./crewDetail/hooks/useCrewDeactivateModal"

export default function CrewMembersPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  // Edit modal hook
  const { isEditModalOpen, selectedCrewMember, handleEdit, handleCancel } = useCrewMemberEdit()

  // Deactivate/Delete modal hook
  const {
    deactivateDeleteModalState,
    activateModalVisible,
    selectedCrew,
    openDeactivateModal,
    openDeleteModal,
    openActivateModal,
    closeDeactivateDeleteModal,
    handleConfirmDeactivateDelete,
    handleActivateConfirm,
    deactivateLoading,
    deleteLoading,
    deactivationDetailsLoading,
    deletionDetailsLoading
  } = useCrewDeactivateModal()

  // Custom hooks for filters with Redux backend integration
  const {
    search,
    selectedRoles,
    selectedStatuses,
    selectedDate,
    pageSize,
    roleOptions,
    statusOptions,
    expandedSelectedRoles,
    expandedSelectedStatuses,
    crewData,
    metadata,
    loading,
    setSearch,
    setSelectedRoles,
    setSelectedStatuses,
    setSelectedDate,
    setCurrentPage,
    setPageSize,
    handleTableChange
  } = useCrewFilters(t)

  // Fetch roles on mount
  useEffect(() => {
    dispatch(getRoles())
  }, [dispatch])

  const handleDeactivate = (record) => {
    openDeactivateModal(record)
  }

  const handleActivate = (record) => {
    openActivateModal(record)
  }

  const handleDelete = (record) => {
    openDeleteModal(record)
  }

  const handleConfirmModal = async (data) => {
    const result = await handleConfirmDeactivateDelete(data)
    if (result?.success) {
      // Refresh the crew list after successful deactivation/deletion
      dispatch(getCrewProfiles({
        search,
        roleIds: selectedRoles,
        status: selectedStatuses.length > 0 ? selectedStatuses[0] : undefined,
        available_on: selectedDate,
        page: metadata.current_page,
        per_page: pageSize
      }))
    }
  }

  const handleConfirmActivate = async () => {
    const result = await handleActivateConfirm()
    if (result?.success) {
      // Refresh the crew list after successful activation
      dispatch(getCrewProfiles({
        search,
        roleIds: selectedRoles,
        status: selectedStatuses.length > 0 ? selectedStatuses[0] : undefined,
        available_on: selectedDate,
        page: metadata.current_page,
        per_page: pageSize
      }))
    }
  }

  return (
    <div className="vstack flex-fill z-0 mt-l" id="clients">
      <FirstLoading>
        <div className="flex-fill">
          <CrewFilters
            search={search}
            onSearchChange={setSearch}
            selectedRoles={selectedRoles}
            onRoleChange={setSelectedRoles}
            roleOptions={roleOptions}
            selectedStatuses={selectedStatuses}
            onStatusChange={setSelectedStatuses}
            statusOptions={statusOptions}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
          />
          <FilterChips selectedValues={expandedSelectedRoles} options={roleOptions} onChange={setSelectedRoles} className="my-m" />
          <FilterChips selectedValues={expandedSelectedStatuses} options={statusOptions} onChange={setSelectedStatuses} className="my-m" />
          {loading && crewData.length === 0 ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              <CrewMembersTable
                data={crewData}
                onChange={handleTableChange}
                loading={loading}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onDeactivate={handleDeactivate}
                onActivate={handleActivate}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
                <CustomPagination
                  metadata={metadata}
                  pageSize={pageSize}
                  total={metadata.total_results || 0}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size)
                    setCurrentPage(1)
                  }}
                  pageSizeOptions={[5, 10, 20, 50]}
                />
              </div>
            </>
          )}
        </div>
        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>
      </FirstLoading>

      {/* Edit Crew Member Modal */}
      {selectedCrewMember && (
        <EditCrewMemberModal
          open={isEditModalOpen}
          onCancel={handleCancel}
          onSave={() => dispatch(getCrewProfiles({
            search,
            roleIds: selectedRoles,
            status: selectedStatuses.length > 0 ? selectedStatuses[0] : undefined,
            available_on: selectedDate,
            page: metadata.current_page,
            per_page: pageSize
          }))}
          crewMember={selectedCrewMember}
          context="crew"
        />
      )}

      {/* Deactivate/Delete/Activate Modals */}
      <DeactivateDeleteModals
        modalState={deactivateDeleteModalState}
        activateModalVisible={activateModalVisible}
        selectedCrew={selectedCrew}
        onClose={closeDeactivateDeleteModal}
        onConfirm={handleConfirmModal}
        onActivateConfirm={handleConfirmActivate}
        deactivateLoading={deactivateLoading}
        deleteLoading={deleteLoading}
        deactivationDetailsLoading={deactivationDetailsLoading}
        deletionDetailsLoading={deletionDetailsLoading}
        t={t}
      />
    </div>
  )
}
